import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { format } from "date-fns";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

import { GenericTable } from "@ui-components/Table/GenericTable";
import { logsColumns, LogItem } from "./LogsData";
import {
  getCTXRlistGlobal,
  getLogsByCtxrId,
} from "./request/request";
import { GenericFilter } from "@ui-components/Filter/GenericFilter";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericDateTimeRangePicker from "@ui-components/DateTime/GenericDateTimeRangePicker";

// <--- Asegúrate de importar tu GenericAlert
import GenericAlert from "@ui-components/Alert/GenericAlert";

interface DateTimeRange {
  start: Date | null;
  end: Date | null;
}

interface CtxrItem {
  id: string;
  name: string;
  description: string;
  subscriptionId: number;
  Suscription?: string;
}

export function LogsPage() {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();

  const [isFetching, setIsFetching] = useState(false);
  const [tablesData, setTablesData] = useState<LogItem[]>([]);

  // Filtro local
  const [searchTerm, setSearchTerm] = useState("");
  const [searchColumn, setSearchColumn] = useState("All");

  // CTXR
  const [fullCTXRlist, setFullCTXRlist] = useState<CtxrItem[]>([]);
  const [firstOptions, setFirstOptions] = useState<string[]>([]);
  const [selectedFirst, setSelectedFirst] = useState("");

  // Severity
  const severityOptions = ["All Levels", "ERROR", "DEBUG", "INFO", "WARNING"];
  const [selectedSeverity, setSelectedSeverity] = useState("All Levels");

  // Búsqueda
  const [searchValue, setSearchValue] = useState("");

  // Rango
  const [dateRange, setDateRange] = useState<DateTimeRange>({
    start: null,
    end: null,
  });

  // Estado para mostrar alert si no hay rango
  const [dateRangeErrorOpen, setDateRangeErrorOpen] = useState(false);

  useEffect(() => {
    if (isLoading || !isAuthenticated) return;

    (async () => {
      setIsFetching(true);
      try {
        const token = await getAccessTokenSilently();
        const resp = await getCTXRlistGlobal(-1, 1, token);
        const dataArray: CtxrItem[] = resp.data || [];

        setFullCTXRlist(dataArray);
        const mapped = dataArray.map(
          (item) => `${item.name} (${item.description})`
        );
        setFirstOptions(mapped);

        if (mapped.length > 0) {
          setSelectedFirst(mapped[0]);
        }
      } catch (err) {
        console.error("Error =>", err);
      } finally {
        setIsFetching(false);
      }
    })();
  }, [isLoading, isAuthenticated, getAccessTokenSilently]);

  const handleSelectFirst = (val: string) => {
    setSelectedFirst(val);
  };

  const handleRefresh = async () => {
    try {
      // En vez de alert => if no date => abre el GenericAlert
      if (!dateRange.start || !dateRange.end) {
        setDateRangeErrorOpen(true);
        return;
      }

      const foundObj = fullCTXRlist.find(
        (obj) => `${obj.name} (${obj.description})` === selectedFirst
      );
      if (!foundObj) {
        console.warn("No CTXR found for", selectedFirst);
        return;
      }

      setIsFetching(true);
      const token = await getAccessTokenSilently();
      const tenantId = 1;

      const startDateStr = format(dateRange.start, "yyyy-MM-dd");
      const startTimeStr = format(dateRange.start, "HH:mm:ss");
      const endDateStr = format(dateRange.end, "yyyy-MM-dd");
      const endTimeStr = format(dateRange.end, "HH:mm:ss");

      const realLevel = selectedSeverity === "All Levels" ? undefined : selectedSeverity;
      const trimmedSearch = searchValue.trim();
      const realSearch = trimmedSearch === "" ? undefined : trimmedSearch;

      const resp = await getLogsByCtxrId(
        token,
        foundObj.id,
        tenantId,
        endDateStr,
        startDateStr,
        startTimeStr,
        endTimeStr,
        realLevel,
        realSearch
      );

      const rawData = resp.data;
      if (!Array.isArray(rawData)) {
        Swal.fire({
          icon: "info",
          title: "No data found",
          text: "No logs found for that date/time range",
          confirmButtonText: "OK",
        });
        setTablesData([]);
        return;
      }

      const shaped: LogItem[] = rawData.map((item) => ({
        timestamp: item.timestamp || "",
        level: item.level || "",
        message: item.log || "",
      }));

      setTablesData(shaped);
      console.log("GET logs =>", shaped);
    } catch (err) {
      console.error("Error =>", err);
      alert("Error => ver consola");
    } finally {
      setIsFetching(false);
    }
  };

  // Filtrado local
  const filteredData = tablesData.filter((row) => {
    const term = searchTerm.toLowerCase();
    if (!term) return true;
    const includes = (val: string) => val.toLowerCase().includes(term);

    switch (searchColumn) {
      case "Timestamp":
        return includes(row.timestamp);
      case "Severity Level":
        return includes(row.level);
      case "Message":
        return includes(row.message);
      case "All":
      default:
        return (
          includes(row.timestamp) ||
          includes(row.level) ||
          includes(row.message)
        );
    }
  });

  const handleDetailsClick = (row: LogItem) => {
    Swal.fire({
      title: "Log Details",
      html: `
        <div style="display: flex; flex-direction: column; align-items: center; max-width: 400px; margin: 0 auto; text-align: center;">
          <div style="white-space: pre-wrap; max-height: 300px; overflow: auto; margin-bottom: 16px; font-size: 1rem; line-height: 1.4;">
            ${row.message}
          </div>
          <div style="display: flex; gap: 16px; justify-content: center; margin-top: 8px;">
            <button id="myCopyBtn" style="padding: 8px 16px; background-color: #6366f1; color: #fff; border: none; border-radius: 4px; cursor: pointer; font-size: 0.875rem; transition: background-color 0.2s;">Copy</button>
            <button id="myCloseBtn" style="padding: 8px 16px; background-color: #6b7280; color: #fff; border: none; border-radius: 4px; cursor: pointer; font-size: 0.875rem; transition: background-color 0.2s;">Close</button>
          </div>
        </div>
      `,
      showConfirmButton: false,
      showCancelButton: false,
      allowOutsideClick: false,
      allowEscapeKey: true,
      didOpen: (popup) => {
        const copyBtn = popup.querySelector("#myCopyBtn") as HTMLButtonElement;
        if (copyBtn) {
          copyBtn.addEventListener("click", () => {
            navigator.clipboard.writeText(row.message || "");
            copyBtn.textContent = "Copied!";
            copyBtn.style.backgroundColor = "#10b981";
            setTimeout(() => {
              copyBtn.textContent = "Copy";
              copyBtn.style.backgroundColor = "#6366f1";
            }, 2000);
          });
        }

        const closeBtn = popup.querySelector("#myCloseBtn") as HTMLButtonElement;
        if (closeBtn) {
          closeBtn.addEventListener("click", () => {
            Swal.close();
          });
        }
      },
    });
  };

  return (
    <div className="relative min-h-[500px]">
      {/* Alert: Falta DateRange */}
      <GenericAlert
        isOpen={dateRangeErrorOpen}
        onClose={() => setDateRangeErrorOpen(false)}
        onConfirm={() => setDateRangeErrorOpen(false)}
        variant="error"
        title="Falta el rango de fecha/hora"
        description="Por favor selecciona un rango de fechas/hora válido."
        confirmLabel="OK"
      />

      <div className="mb-4 p-4 bg-semantic-bg-primary border border-semantic-border-secondary rounded-sm ">
        <div className="flex items-end justify-between">
          <div className="flex items-end space-x-4">
            {/* CTX-R */}
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-semantic-text-primary">
                CTX-R
              </label>
              <GenericDropdown
                items={firstOptions}
                value={selectedFirst}
                onSelect={handleSelectFirst}
                minWidthClass="min-w-[300px]"
                widthClass="w-60"
                defaultLabel="-- Select instance --"
              />
            </div>

            {/* Severity Level */}
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-semantic-text-primary">
                Severity Level
              </label>
              <GenericDropdown
                items={severityOptions}
                value={selectedSeverity}
                onSelect={setSelectedSeverity}
                widthClass="w-60"
              />
            </div>

            {/* Search Log */}
            <div className="flex flex-col">
              <GenericInput
                label="Search Log"
                placeholder="Search logs..."
                value={searchValue}
                onChange={setSearchValue}
                type="search"
              />
            </div>

            {/* Date Range */}
            <div className="flex flex-col">
              <GenericDateTimeRangePicker
                label="Date Range"
                value={dateRange}
                onChange={setDateRange}
                displayFormat="dd/MM/yyyy HH:mm"
              />
            </div>

            {/* Refresh */}
            <GenericButton
              label="Refresh"
              variant="secondary"
              shape="ghost"
              size="md"
              onClick={handleRefresh}
            />
          </div>
        </div>
      </div>

      <div className="mb-4 bg-[var(--semantic-bg-primary)]">
        <div className="flex items-end justify-between">
          <div className="flex items-end space-x-4" />
          <div className="flex items-center space-x-4">
            <GenericFilter
              searchColumn={searchColumn}
              onSearchColumnChange={setSearchColumn}
              searchTerm={searchTerm}
              onSearchTermChange={setSearchTerm}
              columnOptions={["All", "Timestamp", "Severity Level", "Message"]}
            />
          </div>
        </div>
      </div>

      {/* Tabla */}
      <GenericTable<LogItem>
        columns={logsColumns(handleDetailsClick)}
        data={filteredData}
        initialRowsPerPage={5}
        rowsPerPageOptions={[5, 10, 20]}
        width="100%"
        tableClassName="bg-semantic-bg-primary"
      />

      {/* Overlay spinner */}
      {isFetching && (
        <div className="fixed inset-0 flex flex-col items-center justify-center bg-black/50 z-[9999]">
          <h2 className="text-xl font-semibold mb-4 text-white">
            Processing request...
          </h2>
          <div
            className="
              animate-spin
              rounded-full
              h-12 w-12
              border-4 border-gray-300
              border-t-blue-500
            "
          />
        </div>
      )}
    </div>
  );
}

export default LogsPage;
